import { HttpClient } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { cloneDeep } from 'lodash-es';
import { firstValueFrom, take } from 'rxjs';

import { API_URL } from '@constants';
import {
  Billing,
  Community,
  CommunityFrontSubscription,
  CommunitySubscription,
  Invoice,
  PermissionLevel,
  PriceConfiguration,
  UserSettings,
} from '@models';
import { ModalService, UserBeanService } from '@services';
import { logError } from '@shared/utils';

type TransactionParameters = XOR<
  {
    transactionId: string;
  },
  {
    amount: string;
    cancelUrl: string;
    currency: string;
    editSubscription: string;
    errorUrl: string;
    language: string;
    merchantId: string;
    paymentmethod: string;
    refno: string;
    reqtype: string;
    sign: string;
    submitUrl: string;
    successUrl: string;
    uppReturnMaskedCC: string;
    uppReturnTarget: string;
  }
>;
export type BillingAccountEditType = 'billing' | 'contact';

@Service()
export class BillingService {
  modalService = inject(ModalService);
  http = inject(HttpClient);
  userBeanService = inject(UserBeanService);

  createSubscription = (subscription: Record<string, unknown>) =>
    this.http.post<CommunitySubscription>(
      `${API_URL}/billing/subscription`,
      subscription
    );

  updateBilling = (data: Record<string, unknown>) =>
    this.http.put<Billing>(`${API_URL}/billing/update`, data);

  getBilling = () => this.http.get<Billing>(`${API_URL}/billing`);

  getInvoices = () => this.http.get<Invoice[]>(`${API_URL}/billing/invoices`);

  getPrices = () =>
    this.http.get<PriceConfiguration>(`${API_URL}/billing/prices`);

  getTransactionParameters = () =>
    this.http.get<TransactionParameters>(
      `${API_URL}/billing/transactionparameters`
    );

  editBillingAccount = async (
    resource: Billing,
    defaultExpandedAccordionItem: BillingAccountEditType
  ) => {
    const { BillingEditAccountComponent } =
      await import('../../modules/billing/components/billing-edit-account.component');

    return this.modalService.open(
      BillingEditAccountComponent,
      {
        resource: cloneDeep(resource),
        defaultExpandedAccordionItem,
      },
      {
        modalDialogClass: 'lg-modal',
      }
    );
  };

  updateSubscription = (subscription: CommunityFrontSubscription) =>
    this.http
      .post(`${API_URL}/billing/subscription`, subscription)
      .pipe(take(1));

  suspendCommunity = async (community: Community) => {
    const { SuspendCommunityModalComponent } =
      await import('../../modules/billing/components/suspend-community-modal.component');

    return this.modalService.open(SuspendCommunityModalComponent, {
      community,
    });
  };

  getSubscriptions = (params?: Record<string, string | number | boolean>) =>
    firstValueFrom(
      this.http.get<CommunitySubscription[]>(
        `${API_URL}/billing/subscriptions`,
        {
          params,
        }
      )
    );

  getBillingResolver = async () => {
    try {
      const userBean: UserSettings = await this.userBeanService.getUserBean();

      return userBean.communityPermission === PermissionLevel.MODIFY_AND_DELETE
        ? firstValueFrom(this.getBilling())
        : null;
    } catch (error) {
      logError(error);

      return null;
    }
  };
}
